/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from 'componentregistry/registry/Base.type';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSwitch';
import { analyzeActionSwitch } from './ActionSwitchAnalyze';

const ActionSwitchManager: ActionManagerFn<'ActionSwitch'> = (
  platform: Platform,
): ActionManager<'ActionSwitch'> => {
  return {
    ...baseActionManager(platform, 'ActionSwitch'),
    isChildAllowed: (page, item, childType) =>
      ['ActionSwitchCase'].includes(childType as ItemType),
    defaultChildren: ['ActionSwitchCase', 'ActionSwitchCase'],
    defaultValue,
    props: {
      ...baseActionProps,
      expression: {
        label: 'Expression',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          'Must return a string value that will be matched with the child case values',
        required: true,
      },
    },
    name: 'Switch',
    icon: { iconName: 'light-switch' },
    analyze: analyzeActionSwitch,
    description: `**Overview**<br/>
The Switch action is a control flow component designed to implement multi-branch logic within an action chain. It functions exactly like the standard switch statement in JavaScript or other programming languages. Instead of creating a messy nested chain of "If/Else" conditions, this component evaluates a single expression once and then routes execution to the specific matching "Case" child action.<br/><br/>
This component serves as the parent container. It calculates the "test value" and then looks through its children (ActionSwitchCase) to find one with a matching value. If a match is found, the actions inside that specific Case are executed.

**Key Features**<br/>
* **Clean Branching:** ideal for scenarios where a single variable can have many distinct values (e.g., Status = "New", "Pending", "Approved", "Rejected"). It avoids the complexity of stacking multiple "ActionCondition" components.
* **Strict Hierarchy:** Explicitly restricted via isChildAllowed to only accept ActionSwitchCase components as direct children, ensuring a structured and valid logic tree.
* **Dynamic Evaluation:** The expression property is evaluated at runtime, allowing the branching logic to depend on user input, database values, or session variables.

**Properties**<br/>
* **Expression (Required):** An expression property returning a string.
  * *Function:* The value to be tested.
  * *Behavior:* This result is compared against the value property of each child ActionSwitchCase to determine which path to take.

**Use Cases**<br/>
* **Workflow Routing:** Checking the Status of an order. If "New", run validation logic; if "Shipped", show a tracking modal; if "Delivered", open the feedback form.
* **Role-Based Access:** Checking $.session.role. If "Admin", go to the Admin Dashboard; if "Manager", go to the Reports page; if "User", go to the Home page.
* **Option Handling:** Based on a "Payment Method" dropdown selection, showing either the Credit Card form, the PayPal button, or the Bank Transfer instructions.`,
  };
};

export default ActionSwitchManager;
